import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import './documentSymbolsTree.css.js';
import 'vscode/vscode/vs/editor/contrib/symbolIcons/browser/symbolIcons';
import { $, append, hide, show, WindowIdleValue } from 'vscode/vscode/vs/base/browser/dom';
import { HighlightedLabel } from 'vscode/vscode/vs/base/browser/ui/highlightedlabel/highlightedLabel';
import { createMatches } from 'vscode/vscode/vs/base/common/filters';
import { Range } from 'vscode/vscode/vs/editor/common/core/range';
import { getAriaLabelForSymbol, symbolKindNames, SymbolKinds } from 'vscode/vscode/vs/editor/common/languages';
import { OutlineGroup, OutlineModel, OutlineElement } from 'vscode/vscode/vs/editor/contrib/documentSymbols/browser/outlineModel';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IconLabel } from 'vscode/vscode/vs/base/browser/ui/iconLabel/iconLabel';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { MarkerSeverity } from 'vscode/vscode/vs/platform/markers/common/markers';
import { IThemeService } from 'vscode/vscode/vs/platform/theme/common/themeService.service';
import 'vscode/vscode/vs/platform/theme/common/colorUtils';
import 'vscode/vscode/vs/platform/theme/common/colors/baseColors';
import 'vscode/vscode/vs/platform/theme/common/colors/chartsColors';
import 'vscode/vscode/vs/platform/theme/common/colors/editorColors';
import 'vscode/vscode/vs/platform/theme/common/colors/inputColors';
import { listErrorForeground, listWarningForeground } from 'vscode/vscode/vs/platform/theme/common/colors/listColors';
import 'vscode/vscode/vs/platform/theme/common/colors/menuColors';
import 'vscode/vscode/vs/platform/theme/common/colors/minimapColors';
import 'vscode/vscode/vs/platform/theme/common/colors/miscColors';
import 'vscode/vscode/vs/platform/theme/common/colors/quickpickColors';
import 'vscode/vscode/vs/platform/theme/common/colors/searchColors';
import { ITextResourceConfigurationService } from 'vscode/vscode/vs/editor/common/services/textResourceConfiguration';
import { ThemeIcon } from 'vscode/vscode/vs/base/common/themables';
import { mainWindow } from 'vscode/vscode/vs/base/browser/window';

var DocumentSymbolFilter_1;
const _moduleId = "vs/workbench/contrib/codeEditor/browser/outline/documentSymbolsTree";
class DocumentSymbolNavigationLabelProvider {
    getKeyboardNavigationLabel(element) {
        if (element instanceof OutlineGroup) {
            return element.label;
        }
        else {
            return element.symbol.name;
        }
    }
}
class DocumentSymbolAccessibilityProvider {
    constructor(_ariaLabel) {
        this._ariaLabel = _ariaLabel;
    }
    getWidgetAriaLabel() {
        return this._ariaLabel;
    }
    getAriaLabel(element) {
        if (element instanceof OutlineGroup) {
            return element.label;
        }
        else {
            return getAriaLabelForSymbol(element.symbol.name, element.symbol.kind);
        }
    }
}
class DocumentSymbolIdentityProvider {
    getId(element) {
        return element.id;
    }
}
class DocumentSymbolGroupTemplate {
    static { this.id = 'DocumentSymbolGroupTemplate'; }
    constructor(labelContainer, label) {
        this.labelContainer = labelContainer;
        this.label = label;
    }
    dispose() {
        this.label.dispose();
    }
}
class DocumentSymbolTemplate {
    static { this.id = 'DocumentSymbolTemplate'; }
    constructor(container, iconLabel, iconClass, decoration) {
        this.container = container;
        this.iconLabel = iconLabel;
        this.iconClass = iconClass;
        this.decoration = decoration;
    }
}
class DocumentSymbolVirtualDelegate {
    getHeight(_element) {
        return 22;
    }
    getTemplateId(element) {
        return element instanceof OutlineGroup
            ? DocumentSymbolGroupTemplate.id
            : DocumentSymbolTemplate.id;
    }
}
class DocumentSymbolGroupRenderer {
    constructor() {
        this.templateId = DocumentSymbolGroupTemplate.id;
    }
    renderTemplate(container) {
        const labelContainer = $('.outline-element-label');
        container.classList.add('outline-element');
        append(container, labelContainer);
        return (
             (new DocumentSymbolGroupTemplate(labelContainer,  (new HighlightedLabel(labelContainer))))
        );
    }
    renderElement(node, _index, template) {
        template.label.set(node.element.label, createMatches(node.filterData));
    }
    disposeTemplate(_template) {
        _template.dispose();
    }
}
let DocumentSymbolRenderer = class DocumentSymbolRenderer {
    constructor(_renderMarker, target, _configurationService, _themeService) {
        this._renderMarker = _renderMarker;
        this._configurationService = _configurationService;
        this._themeService = _themeService;
        this.templateId = DocumentSymbolTemplate.id;
    }
    renderTemplate(container) {
        container.classList.add('outline-element');
        const iconLabel = ( (new IconLabel(container, { supportHighlights: true })));
        const iconClass = $('.outline-element-icon');
        const decoration = $('.outline-element-decoration');
        container.prepend(iconClass);
        container.appendChild(decoration);
        return (
             (new DocumentSymbolTemplate(container, iconLabel, iconClass, decoration))
        );
    }
    renderElement(node, _index, template) {
        const { element } = node;
        const extraClasses = ['nowrap'];
        const options = {
            matches: createMatches(node.filterData),
            labelEscapeNewLines: true,
            extraClasses,
            title: ( localizeWithPath(
                _moduleId,
                0,
                "{0} ({1})",
                element.symbol.name,
                symbolKindNames[element.symbol.kind]
            ))
        };
        if (this._configurationService.getValue("outline.icons" )) {
            template.iconClass.className = '';
            template.iconClass.classList.add('outline-element-icon', 'inline', ...ThemeIcon.asClassNameArray(SymbolKinds.toIcon(element.symbol.kind)));
        }
        if (element.symbol.tags.indexOf(1 ) >= 0) {
            extraClasses.push(`deprecated`);
            options.matches = [];
        }
        template.iconLabel.setLabel(element.symbol.name, element.symbol.detail, options);
        if (this._renderMarker) {
            this._renderMarkerInfo(element, template);
        }
    }
    _renderMarkerInfo(element, template) {
        if (!element.marker) {
            hide(template.decoration);
            template.container.style.removeProperty('--outline-element-color');
            return;
        }
        const { count, topSev } = element.marker;
        const color = this._themeService.getColorTheme().getColor(topSev === MarkerSeverity.Error ? listErrorForeground : listWarningForeground);
        const cssColor = color ? ( (color.toString())) : 'inherit';
        const problem = this._configurationService.getValue('problems.visibility');
        const configProblems = this._configurationService.getValue("outline.problems.colors" );
        if (!problem || !configProblems) {
            template.container.style.removeProperty('--outline-element-color');
        }
        else {
            template.container.style.setProperty('--outline-element-color', cssColor);
        }
        if (problem === undefined) {
            return;
        }
        const configBadges = this._configurationService.getValue("outline.problems.badges" );
        if (!configBadges || !problem) {
            hide(template.decoration);
        }
        else if (count > 0) {
            show(template.decoration);
            template.decoration.classList.remove('bubble');
            template.decoration.innerText = count < 10 ? ( (count.toString())) : '+9';
            template.decoration.title = count === 1 ? ( localizeWithPath(_moduleId, 1, "1 problem in this element")) : ( localizeWithPath(_moduleId, 2, "{0} problems in this element", count));
            template.decoration.style.setProperty('--outline-element-color', cssColor);
        }
        else {
            show(template.decoration);
            template.decoration.classList.add('bubble');
            template.decoration.innerText = '\uea71';
            template.decoration.title = ( localizeWithPath(_moduleId, 3, "Contains elements with problems"));
            template.decoration.style.setProperty('--outline-element-color', cssColor);
        }
    }
    disposeTemplate(_template) {
        _template.iconLabel.dispose();
    }
};
DocumentSymbolRenderer = ( (__decorate([
    ( (__param(2, IConfigurationService))),
    ( (__param(3, IThemeService)))
], DocumentSymbolRenderer)));
let DocumentSymbolFilter = class DocumentSymbolFilter {
    static { DocumentSymbolFilter_1 = this; }
    static { this.kindToConfigName = ( (Object.freeze({
        [0 ]: 'showFiles',
        [1 ]: 'showModules',
        [2 ]: 'showNamespaces',
        [3 ]: 'showPackages',
        [4 ]: 'showClasses',
        [5 ]: 'showMethods',
        [6 ]: 'showProperties',
        [7 ]: 'showFields',
        [8 ]: 'showConstructors',
        [9 ]: 'showEnums',
        [10 ]: 'showInterfaces',
        [11 ]: 'showFunctions',
        [12 ]: 'showVariables',
        [13 ]: 'showConstants',
        [14 ]: 'showStrings',
        [15 ]: 'showNumbers',
        [16 ]: 'showBooleans',
        [17 ]: 'showArrays',
        [18 ]: 'showObjects',
        [19 ]: 'showKeys',
        [20 ]: 'showNull',
        [21 ]: 'showEnumMembers',
        [22 ]: 'showStructs',
        [23 ]: 'showEvents',
        [24 ]: 'showOperators',
        [25 ]: 'showTypeParameters',
    }))); }
    constructor(_prefix, _textResourceConfigService) {
        this._prefix = _prefix;
        this._textResourceConfigService = _textResourceConfigService;
    }
    filter(element) {
        const outline = OutlineModel.get(element);
        if (!(element instanceof OutlineElement)) {
            return true;
        }
        const configName = DocumentSymbolFilter_1.kindToConfigName[element.symbol.kind];
        const configKey = `${this._prefix}.${configName}`;
        return this._textResourceConfigService.getValue(outline?.uri, configKey);
    }
};
DocumentSymbolFilter = DocumentSymbolFilter_1 = ( (__decorate([
    ( (__param(1, ITextResourceConfigurationService)))
], DocumentSymbolFilter)));
class DocumentSymbolComparator {
    constructor() {
        this._collator = new WindowIdleValue(mainWindow, () => new Intl.Collator(undefined, { numeric: true }));
    }
    compareByPosition(a, b) {
        if (a instanceof OutlineGroup && b instanceof OutlineGroup) {
            return a.order - b.order;
        }
        else if (a instanceof OutlineElement && b instanceof OutlineElement) {
            return Range.compareRangesUsingStarts(a.symbol.range, b.symbol.range) || this._collator.value.compare(a.symbol.name, b.symbol.name);
        }
        return 0;
    }
    compareByType(a, b) {
        if (a instanceof OutlineGroup && b instanceof OutlineGroup) {
            return a.order - b.order;
        }
        else if (a instanceof OutlineElement && b instanceof OutlineElement) {
            return a.symbol.kind - b.symbol.kind || this._collator.value.compare(a.symbol.name, b.symbol.name);
        }
        return 0;
    }
    compareByName(a, b) {
        if (a instanceof OutlineGroup && b instanceof OutlineGroup) {
            return a.order - b.order;
        }
        else if (a instanceof OutlineElement && b instanceof OutlineElement) {
            return this._collator.value.compare(a.symbol.name, b.symbol.name) || Range.compareRangesUsingStarts(a.symbol.range, b.symbol.range);
        }
        return 0;
    }
}

export { DocumentSymbolAccessibilityProvider, DocumentSymbolComparator, DocumentSymbolFilter, DocumentSymbolGroupRenderer, DocumentSymbolIdentityProvider, DocumentSymbolNavigationLabelProvider, DocumentSymbolRenderer, DocumentSymbolVirtualDelegate };
